import { useEffect, useState } from "react";

export const PanelPosition = {
  LEFT: "left",
  RIGHT: "right",
} as const;
export type PanelPosition = (typeof PanelPosition)[keyof typeof PanelPosition];

interface UseResizablePanelWidthOptions {
  minWidth: number;
  maxWidth: number;
  initialWidth: number;
  // Which side of the screen the panel sits on. Determines which direction
  // dragging the handle grows the panel, since the handle sits on the edge
  // facing the rest of the page.
  position: PanelPosition;
  onResizeEnd?: (width: number) => void;
}

interface UseResizablePanelWidthResult {
  width: number;
  handleDragStart: (e: React.MouseEvent) => void;
}

// Drag-to-resize mechanics for a fixed-width side panel: RAF-throttled width
// updates during the drag (applied directly to the panel element's style to
// avoid a re-render per mousemove), committed to React state on mouseup.
export function useResizablePanelWidth({
  minWidth,
  maxWidth,
  initialWidth,
  position,
  onResizeEnd,
}: UseResizablePanelWidthOptions): UseResizablePanelWidthResult {
  const [width, setWidth] = useState(initialWidth);
  const [activeDragCleanup, setActiveDragCleanup] = useState<(() => void) | null>(null);

  useEffect(() => {
    return () => {
      activeDragCleanup?.();
    };
  }, [activeDragCleanup]);

  const handleDragStart = (e: React.MouseEvent) => {
    e.preventDefault();
    activeDragCleanup?.();

    const startX = e.clientX;
    const startWidth = width;
    const panelEl = e.currentTarget.parentElement as HTMLDivElement | null;
    let latestWidth = startWidth;
    let pendingWidth: number | null = null;
    let rafId: number | null = null;

    const onMove = (ev: MouseEvent) => {
      const delta = position === PanelPosition.RIGHT ? startX - ev.clientX : ev.clientX - startX;
      latestWidth = Math.max(minWidth, Math.min(maxWidth, startWidth + delta));
      pendingWidth = latestWidth;

      if (rafId == null) {
        rafId = window.requestAnimationFrame(() => {
          if (panelEl && pendingWidth != null) {
            panelEl.style.width = `${pendingWidth}px`;
          }
          rafId = null;
        });
      }
    };

    const cleanup = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      if (rafId != null) {
        window.cancelAnimationFrame(rafId);
        rafId = null;
      }
    };

    const onUp = () => {
      cleanup();
      setWidth(latestWidth);
      setActiveDragCleanup(null);
      onResizeEnd?.(latestWidth);
    };

    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    setActiveDragCleanup(() => cleanup);
  };

  return { width, handleDragStart };
}
